import React, { useMemo, useState } from "react";
import { apiPost } from "../api";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { BtnSpinner } from "../components/BtnSpinner";

type LoginSuccess = { ok: true };
type LoginNeeds2fa = { requires2fa: true; tempToken: string };
type LoginResp = LoginSuccess | LoginNeeds2fa;
type Verify2faResp = { ok: true };

function isLoginNeeds2fa(d: LoginResp): d is LoginNeeds2fa {
  return (
    typeof d === "object" &&
    d !== null &&
    (d as LoginNeeds2fa).requires2fa === true &&
    typeof (d as LoginNeeds2fa).tempToken === "string"
  );
}

type Step = "credentials" | "totp";

export function LoginView(props: { locale: Locale; onAuthed: (email: string) => void }) {
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => email.trim().length > 3 && password.length >= 8,
    [email, password]
  );
  const canSubmitCode = useMemo(() => code.trim().length > 0, [code]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await apiPost<LoginResp>("/v1/session", { email, password });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      if (isLoginNeeds2fa(res.data)) {
        setTempToken(res.data.tempToken);
        setCode("");
        setUseBackup(false);
        setStep("totp");
        return;
      }
      props.onAuthed(email.trim());
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!tempToken) {
      setError(t(props.locale, "login.2fa.restart"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const res = await apiPost<Verify2faResp>("/v1/session/verify-2fa", {
        tempToken,
        code: code.trim(),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setError(t(props.locale, "login.2fa.rateLimited"));
        } else {
          setError(res.error);
        }
        return;
      }
      props.onAuthed(email.trim());
    } finally {
      setBusy(false);
    }
  }

  function backToCredentials() {
    setStep("credentials");
    setTempToken(null);
    setCode("");
    setUseBackup(false);
    setError(null);
  }

  function toggleBackup() {
    setUseBackup((v) => !v);
    setCode("");
    setError(null);
  }

  function getForgotPasswordLink() {
    const params = new URLSearchParams();
    if (email.trim()) {
      params.set("email", email.trim().toLowerCase());
    }
    const queryString = params.toString();
    return `/recover${queryString ? "?" + queryString : ""}`;
  }

  const tipText = t(props.locale, "login.tip");
  const hasTip = tipText.trim().length > 0;

  return (
    <div className="authShell">
      <div className="authBg" />
      <div className="authWrap">
        <a className="authBrand" href="/" aria-label="KVEJA home">
          <img className="authLogo" src="/brand/kveja_logo.png" alt="KVEJA" />
        </a>

        <div className="authCard">
          {step === "credentials" ? (
            <>
              <div className="authHeader">
                <h1 className="authTitle">{t(props.locale, "login.title")}</h1>
                <p className="authSubtitle">{t(props.locale, "login.subtitle")}</p>
              </div>

              <form className="authForm" onSubmit={onSubmit}>
                <div className="authField">
                  <div className="authLabel">{t(props.locale, "login.email")}</div>
                  <input
                    className="authInput"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    placeholder="you@domain.com"
                    required
                  />
                </div>

                <div className="authField">
                  <div className="authLabel">{t(props.locale, "login.password")}</div>
                  <input
                    className="authInput"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    autoComplete="current-password"
                    placeholder="Minimum 8 characters"
                    required
                  />
                </div>

                {error ? <div className="authError">{error}</div> : null}

                <div className="authRow">
                  {hasTip ? <div className="authTip">{tipText}</div> : <div /> }
                  <button className="btn primary authBtn" disabled={!canSubmit || busy} type="submit">
                    <BtnSpinner loading={busy}>{busy ? t(props.locale, "login.submitting") : t(props.locale, "login.submit")}</BtnSpinner>
                  </button>
                </div>

                <div className="authRow authRowBottom">
                  <a className="btn authBtnSecondary" href="/signup">{t(props.locale, "login.createAccount")}</a>
                  <a className="small" href={getForgotPasswordLink()}>{t(props.locale, "login.forgotPassword")}</a>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="authHeader">
                <h1 className="authTitle">{t(props.locale, "login.2fa.title")}</h1>
                <p className="authSubtitle">{t(props.locale, "login.2fa.subtitle")}</p>
              </div>

              <form className="authForm" onSubmit={onSubmitCode}>
                <div className="authField">
                  <div className="authLabel">
                    {useBackup
                      ? t(props.locale, "login.2fa.backupLabel")
                      : t(props.locale, "login.2fa.codeLabel")}
                  </div>
                  <input
                    className="authInput"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    type="text"
                    inputMode={useBackup ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </div>

                {error ? (
                  <>
                    <div className="authError">{error}</div>
                    <div className="authTip">{t(props.locale, "login.2fa.restart")}</div>
                  </>
                ) : null}

                <div className="authRow">
                  <button
                    className="btn authBtnSecondary"
                    type="button"
                    onClick={toggleBackup}
                    disabled={busy}
                  >
                    {useBackup
                      ? t(props.locale, "login.2fa.useAuthenticator")
                      : t(props.locale, "login.2fa.useBackup")}
                  </button>
                  <button className="btn primary authBtn" disabled={!canSubmitCode || busy} type="submit">
                    <BtnSpinner loading={busy}>
                      {busy ? t(props.locale, "login.submitting") : t(props.locale, "login.2fa.verify")}
                    </BtnSpinner>
                  </button>
                </div>

                <div className="authRow authRowBottom">
                  <button
                    className="btn authBtnSecondary"
                    type="button"
                    onClick={backToCredentials}
                    disabled={busy}
                  >
                    {t(props.locale, "login.2fa.back")}
                  </button>
                  <a className="small" href={getForgotPasswordLink()}>{t(props.locale, "login.forgotPassword")}</a>
                </div>
              </form>
            </>
          )}
        </div>

        <div className="authFooter">
          <span className="small">KVEJA</span>
          <span className="authDot" aria-hidden="true">·</span>
          <a className="small" href="/">Back to landing</a>
        </div>
      </div>
    </div>
  );
}
